import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  Mail, Send, Inbox, Trash2, FileWarning, FolderOpen, RefreshCw,
  ChevronLeft, Plus, X, Reply, ArrowLeft, Paperclip, Settings, Eye
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function WebMail({ getAuthHeaders }) {
  const { t } = useLanguage();
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState('INBOX');
  const [messages, setMessages] = useState([]);
  const [totalMessages, setTotalMessages] = useState(0);
  const [page, setPage] = useState(1);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('list'); // list, detail, compose
  const [composeData, setComposeData] = useState({ to: '', cc: '', subject: '', body: '', inReplyTo: '' });
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [accountForm, setAccountForm] = useState({ email: '', password: '', name: '', imapHost: 'imap9.gmoserver.jp', imapPort: 993, smtpHost: 'smtp9.gmoserver.jp', smtpPort: 465 });

  const fetchAccounts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/mail/accounts`, { headers: getAuthHeaders() });
      setAccounts(data);
      if (data.length > 0 && !selectedAccount) setSelectedAccount(data[0]);
    } catch (e) { /* ignore */ }
  }, [getAuthHeaders, selectedAccount]);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const fetchFolders = useCallback(async () => {
    if (!selectedAccount) return;
    try {
      const { data } = await axios.get(`${API}/admin/mail/folders?account_id=${selectedAccount.id}`, { headers: getAuthHeaders() });
      setFolders(data);
    } catch (e) { toast.error(t('webmail.folderFetchFailed')); }
  }, [selectedAccount, getAuthHeaders]);

  const fetchMessages = useCallback(async (folder, pg) => {
    if (!selectedAccount) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/mail/messages?account_id=${selectedAccount.id}&folder=${folder || selectedFolder}&page=${pg || page}`, { headers: getAuthHeaders() });
      setMessages(data.messages);
      setTotalMessages(data.total);
    } catch (e) { toast.error(t('webmail.messageFetchFailed')); }
    setLoading(false);
  }, [selectedAccount, selectedFolder, page, getAuthHeaders]);

  useEffect(() => { if (selectedAccount) { fetchFolders(); fetchMessages(); } }, [selectedAccount, selectedFolder, page]);

  const handleSelectFolder = (name) => { setSelectedFolder(name); setPage(1); setView('list'); setSelectedMessage(null); };

  const handleSelectMessage = async (msg) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/mail/messages/${msg.uid}?account_id=${selectedAccount.id}&folder=${selectedFolder}`, { headers: getAuthHeaders() });
      setSelectedMessage(data);
      setView('detail');
    } catch (e) { toast.error(t('webmail.messageFetchFailed')); }
    setLoading(false);
  };

  const handleSend = async () => {
    if (!composeData.to) { toast.error(t('webmail.enterRecipient')); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/admin/mail/send`, { accountId: selectedAccount.id, ...composeData }, { headers: getAuthHeaders() });
      toast.success(t('webmail.sent'));
      setView('list');
      setComposeData({ to: '', cc: '', subject: '', body: '', inReplyTo: '' });
    } catch (e) { toast.error(e.response?.data?.detail || t('webmail.sendFailed')); }
    setLoading(false);
  };

  const handleDelete = async (uid) => {
    try {
      await axios.delete(`${API}/admin/mail/messages/${uid}?account_id=${selectedAccount.id}&folder=${selectedFolder}`, { headers: getAuthHeaders() });
      toast.success(t('webmail.deleted'));
      setView('list');
      setSelectedMessage(null);
      fetchMessages();
    } catch (e) { toast.error(t('webmail.deleteFailed')); }
  };

  const handleReply = () => {
    if (!selectedMessage) return;
    setComposeData({
      to: selectedMessage.fromEmail,
      cc: '',
      subject: selectedMessage.subject?.startsWith('Re:') ? selectedMessage.subject : `Re: ${selectedMessage.subject}`,
      body: `\n\n---\n${selectedMessage.from} (${selectedMessage.date}):\n${selectedMessage.bodyText || ''}`,
      inReplyTo: selectedMessage.messageId || ''
    });
    setView('compose');
  };

  const handleAddAccount = async () => {
    try {
      await axios.post(`${API}/admin/mail/accounts`, accountForm, { headers: getAuthHeaders() });
      toast.success(t('webmail.accountAdded'));
      setAccountDialogOpen(false);
      setAccountForm({ email: '', password: '', name: '', imapHost: 'imap9.gmoserver.jp', imapPort: 993, smtpHost: 'smtp9.gmoserver.jp', smtpPort: 465 });
      fetchAccounts();
    } catch (e) { toast.error(e.response?.data?.detail || t('webmail.addFailed')); }
  };

  const handleDeleteAccount = async (id) => {
    if (!window.confirm(t('webmail.deleteAccountConfirm'))) return;
    try {
      await axios.delete(`${API}/admin/mail/accounts/${id}`, { headers: getAuthHeaders() });
      toast.success(t('webmail.deleted'));
      if (selectedAccount?.id === id) setSelectedAccount(null);
      fetchAccounts();
    } catch (e) { toast.error(t('webmail.deleteFailed')); }
  };

  const folderIcon = (name) => {
    switch(name) {
      case 'INBOX': return <Inbox className="h-4 w-4" />;
      case 'Sent': return <Send className="h-4 w-4" />;
      case 'Trash': return <Trash2 className="h-4 w-4" />;
      case 'Junk': return <FileWarning className="h-4 w-4" />;
      default: return <FolderOpen className="h-4 w-4" />;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      const now = new Date();
      if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
      return d.toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' });
    } catch { return dateStr; }
  };

  // No accounts yet
  if (accounts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-96 space-y-4">
        <Mail className="h-16 w-16 text-gray-300" />
        <p className="text-gray-500">{t('webmail.noAccounts')}</p>
        <Button data-testid="add-first-account-btn" onClick={() => setAccountDialogOpen(true)} className="bg-[#00A651] hover:bg-[#008a43]">
          <Plus className="h-4 w-4 mr-1" /> {t('webmail.addAccount')}
        </Button>
        <AccountDialog open={accountDialogOpen} onOpenChange={setAccountDialogOpen} form={accountForm} setForm={setAccountForm} onSave={handleAddAccount} t={t} />
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-180px)] border rounded-lg overflow-hidden bg-white" data-testid="webmail-container">
      {/* Sidebar */}
      <div className="w-56 border-r bg-gray-50 flex flex-col flex-shrink-0">
        {/* Account selector */}
        <div className="p-3 border-b">
          <select
            data-testid="mail-account-select"
            className="w-full text-sm border rounded px-2 py-1.5"
            value={selectedAccount?.id || ''}
            onChange={e => { const a = accounts.find(x => x.id === e.target.value); setSelectedAccount(a); }}
          >
            {accounts.map(a => <option key={a.id} value={a.id}>{a.email}</option>)}
          </select>
        </div>

        {/* Compose button */}
        <div className="p-3">
          <Button
            data-testid="compose-btn"
            className="w-full bg-[#00A651] hover:bg-[#008a43] text-sm"
            onClick={() => { setComposeData({ to: '', cc: '', subject: '', body: '', inReplyTo: '' }); setView('compose'); }}
          >
            <Plus className="h-4 w-4 mr-1" /> {t('webmail.compose')}
          </Button>
        </div>

        {/* Folders */}
        <div className="flex-1 overflow-y-auto">
          {folders.map(f => (
            <button
              key={f.name}
              data-testid={`folder-${f.name}`}
              className={`w-full flex items-center gap-2 px-4 py-2 text-sm text-left hover:bg-gray-100 transition ${selectedFolder === f.name ? 'bg-green-50 text-[#00A651] font-medium border-r-2 border-[#00A651]' : 'text-gray-700'}`}
              onClick={() => handleSelectFolder(f.name)}
            >
              {folderIcon(f.name)}
              <span className="flex-1 truncate">{
                f.name === 'INBOX' ? t('webmail.inbox') :
                f.name === 'Sent' ? t('webmail.sentFolder') :
                f.name === 'Drafts' ? t('webmail.drafts') :
                f.name === 'Trash' ? t('webmail.trash') :
                f.name === 'Junk' ? t('webmail.junk') :
                f.displayName
              }</span>
              {f.unseen > 0 && <Badge className="bg-[#00A651] text-white text-xs px-1.5 py-0">{f.unseen}</Badge>}
            </button>
          ))}
        </div>

        {/* Bottom actions */}
        <div className="p-2 border-t space-y-1">
          <Button variant="outline" size="sm" className="w-full text-xs" onClick={() => setAccountDialogOpen(true)} data-testid="manage-accounts-btn">
            <Plus className="h-3 w-3 mr-1" /> {t('webmail.addAccount')}
          </Button>
          <Button variant="ghost" size="sm" className="w-full text-xs" onClick={() => { fetchFolders(); fetchMessages(); }} title={t('webmail.refresh')}>
            <RefreshCw className="h-3 w-3 mr-1" /> {t('webmail.refresh')}
          </Button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {view === 'list' && (
          <>
            <div className="p-3 border-b flex items-center justify-between bg-white">
              <h3 className="font-medium text-sm">{
                selectedFolder === 'INBOX' ? t('webmail.inbox') :
                selectedFolder === 'Sent' ? t('webmail.sentFolder') :
                selectedFolder === 'Drafts' ? t('webmail.drafts') :
                selectedFolder === 'Trash' ? t('webmail.trash') :
                selectedFolder === 'Junk' ? t('webmail.junk') :
                folders.find(f => f.name === selectedFolder)?.displayName || selectedFolder
              } ({totalMessages})</h3>
              <div className="flex items-center gap-2 text-xs text-gray-500">
                {page > 1 && <Button variant="ghost" size="sm" onClick={() => setPage(p => p - 1)}>&lt;</Button>}
                <span>{(page-1)*30+1}-{Math.min(page*30, totalMessages)}</span>
                {page * 30 < totalMessages && <Button variant="ghost" size="sm" onClick={() => setPage(p => p + 1)}>&gt;</Button>}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center h-32 text-gray-400">{t('webmail.loading')}</div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-32 text-gray-400">
                  <Mail className="h-8 w-8 mb-2" />
                  <span className="text-sm">{t('webmail.noMessages')}</span>
                </div>
              ) : (
                messages.map(msg => (
                  <div
                    key={msg.uid}
                    data-testid={`mail-item-${msg.uid}`}
                    className={`flex items-center gap-3 px-4 py-3 border-b cursor-pointer hover:bg-gray-50 transition ${!msg.seen ? 'bg-blue-50/50 font-medium' : ''}`}
                    onClick={() => handleSelectMessage(msg)}
                  >
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${!msg.seen ? 'bg-[#00A651]' : 'bg-transparent'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-sm truncate max-w-[200px]">{msg.from}</span>
                        <span className="text-xs text-gray-400 flex-shrink-0 ml-2">{formatDate(msg.date)}</span>
                      </div>
                      <p className="text-sm text-gray-600 truncate">{msg.subject}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}

        {view === 'detail' && selectedMessage && (
          <>
            <div className="p-3 border-b flex items-center gap-2 bg-white">
              <Button variant="ghost" size="sm" onClick={() => { setView('list'); setSelectedMessage(null); fetchMessages(); }}>
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="flex-1" />
              <Button variant="ghost" size="sm" onClick={handleReply} data-testid="reply-btn">
                <Reply className="h-4 w-4 mr-1" /> {t('webmail.reply')}
              </Button>
              <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(selectedMessage.uid)} data-testid="delete-msg-btn">
                <Trash2 className="h-4 w-4 mr-1" /> {t('webmail.delete')}
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <h2 className="text-lg font-medium mb-3">{selectedMessage.subject}</h2>
              <div className="flex items-start gap-3 mb-4 pb-3 border-b">
                <div className="w-10 h-10 rounded-full bg-[#00A651] text-white flex items-center justify-center text-sm font-medium flex-shrink-0">
                  {(selectedMessage.from || '?')[0].toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{selectedMessage.from}</div>
                  <div className="text-xs text-gray-500">{selectedMessage.fromEmail}</div>
                  <div className="text-xs text-gray-400 mt-0.5">To: {selectedMessage.to}</div>
                  {selectedMessage.cc && <div className="text-xs text-gray-400">Cc: {selectedMessage.cc}</div>}
                  <div className="text-xs text-gray-400">{selectedMessage.date}</div>
                </div>
              </div>
              {selectedMessage.attachments?.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4 pb-3 border-b">
                  {selectedMessage.attachments.map((a, i) => (
                    <div key={i} className="flex items-center gap-1 bg-gray-100 rounded px-2 py-1 text-xs">
                      <Paperclip className="h-3 w-3" /> {a.filename} ({(a.size / 1024).toFixed(1)}KB)
                    </div>
                  ))}
                </div>
              )}
              <div className="prose prose-sm max-w-none">
                {selectedMessage.bodyHtml ? (
                  <div dangerouslySetInnerHTML={{ __html: selectedMessage.bodyHtml }} className="mail-body" />
                ) : (
                  <pre className="whitespace-pre-wrap text-sm text-gray-700 font-sans">{selectedMessage.bodyText}</pre>
                )}
              </div>
            </div>
          </>
        )}

        {view === 'compose' && (
          <>
            <div className="p-3 border-b flex items-center gap-2 bg-white">
              <Button variant="ghost" size="sm" onClick={() => setView('list')}>
                <X className="h-4 w-4 mr-1" /> {t('webmail.cancel')}
              </Button>
              <div className="flex-1" />
              <Button className="bg-[#00A651] hover:bg-[#008a43]" onClick={handleSend} disabled={loading} data-testid="send-btn">
                <Send className="h-4 w-4 mr-1" /> {loading ? t('webmail.sending') : t('webmail.send')}
              </Button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              <div className="flex items-center gap-2">
                <Label className="w-12 text-right text-sm text-gray-500">To:</Label>
                <Input
                  data-testid="compose-to"
                  value={composeData.to}
                  onChange={e => setComposeData(d => ({ ...d, to: e.target.value }))}
                  placeholder="recipient@example.com"
                  className="flex-1"
                />
              </div>
              <div className="flex items-center gap-2">
                <Label className="w-12 text-right text-sm text-gray-500">Cc:</Label>
                <Input
                  data-testid="compose-cc"
                  value={composeData.cc}
                  onChange={e => setComposeData(d => ({ ...d, cc: e.target.value }))}
                  placeholder="cc@example.com"
                  className="flex-1"
                />
              </div>
              <div className="flex items-center gap-2">
                <Label className="w-12 text-right text-sm text-gray-500">{t('webmail.subject')}:</Label>
                <Input
                  data-testid="compose-subject"
                  value={composeData.subject}
                  onChange={e => setComposeData(d => ({ ...d, subject: e.target.value }))}
                  placeholder={t('webmail.subjectPlaceholder')}
                  className="flex-1"
                />
              </div>
              <Textarea
                data-testid="compose-body"
                value={composeData.body}
                onChange={e => setComposeData(d => ({ ...d, body: e.target.value }))}
                placeholder={t('webmail.bodyPlaceholder')}
                className="min-h-[300px] mt-2"
              />
            </div>
          </>
        )}
      </div>

      {/* Account Dialog */}
      <AccountDialog
        open={accountDialogOpen}
        onOpenChange={setAccountDialogOpen}
        form={accountForm}
        setForm={setAccountForm}
        onSave={handleAddAccount}
        accounts={accounts}
        onDelete={handleDeleteAccount}
        t={t}
      />
    </div>
  );
}

function AccountDialog({ open, onOpenChange, form, setForm, onSave, accounts = [], onDelete, t }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('webmail.accountManagement')}</DialogTitle>
        </DialogHeader>
        {accounts.length > 0 && (
          <div className="space-y-2 mb-4">
            <Label className="text-sm text-gray-500">{t('webmail.registeredAccounts')}</Label>
            {accounts.map(a => (
              <div key={a.id} className="flex items-center justify-between bg-gray-50 rounded px-3 py-2">
                <span className="text-sm">{a.email}</span>
                <Button variant="ghost" size="sm" className="text-red-500 h-7" onClick={() => onDelete?.(a.id)}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        )}
        <div className="space-y-3">
          <Label className="text-sm font-medium">{t('webmail.addNewAccount')}</Label>
          <div>
            <Label className="text-xs">{t('webmail.emailAddress')}</Label>
            <Input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="user@jwsea.jp" className="mt-1" />
          </div>
          <div>
            <Label className="text-xs">{t('webmail.password')}</Label>
            <Input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} className="mt-1" />
          </div>
          <div>
            <Label className="text-xs">{t('webmail.displayName')}</Label>
            <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="JWSEA Foundation" className="mt-1" />
          </div>
          <details className="text-xs">
            <summary className="cursor-pointer text-gray-500">{t('webmail.serverSettings')}</summary>
            <div className="grid grid-cols-2 gap-2 mt-2">
              <div>
                <Label className="text-xs">{t('webmail.imapServer')}</Label>
                <Input value={form.imapHost} onChange={e => setForm(f => ({ ...f, imapHost: e.target.value }))} className="mt-1 text-xs" />
              </div>
              <div>
                <Label className="text-xs">{t('webmail.imapPort')}</Label>
                <Input type="number" value={form.imapPort} onChange={e => setForm(f => ({ ...f, imapPort: parseInt(e.target.value) }))} className="mt-1 text-xs" />
              </div>
              <div>
                <Label className="text-xs">{t('webmail.smtpServer')}</Label>
                <Input value={form.smtpHost} onChange={e => setForm(f => ({ ...f, smtpHost: e.target.value }))} className="mt-1 text-xs" />
              </div>
              <div>
                <Label className="text-xs">{t('webmail.smtpPort')}</Label>
                <Input type="number" value={form.smtpPort} onChange={e => setForm(f => ({ ...f, smtpPort: parseInt(e.target.value) }))} className="mt-1 text-xs" />
              </div>
            </div>
          </details>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('webmail.close')}</Button>
          <Button className="bg-[#00A651] hover:bg-[#008a43]" onClick={onSave} disabled={!form.email || !form.password}>{t('webmail.add')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
